import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, PasswordInput, Spinner, Toggle } from '../../components';
import { ASSETS } from '../../constants/assets';
import {
  formatPasswordStatusText,
  getProfileErrorMessage,
  profileService,
} from '../../services/profileService';
import { sharedService } from '../../services/sharedService';
import { useAuthStore } from '../../store/authStore';
import type { AdminNotificationSettings } from '../../types/profile';
import { PasswordRequirements } from '../auth/PasswordRequirements';
import { PasswordStrength } from '../auth/PasswordStrength';
import { isPasswordValid } from '../../utils/passwordValidation';
import { getFieldErrorsFromApi } from '../../utils/errorHandler';
import './Settings.scss';

type SettingsTab = 'profile' | 'security' | 'notifications';

type FeedbackState = {
  type: 'success' | 'error';
  message: string;
} | null;

const SETTINGS_TABS: { key: SettingsTab; label: string }[] = [
  { key: 'profile', label: 'My Profile' },
  { key: 'security', label: 'Security' },
  { key: 'notifications', label: 'Notifications' },
];

const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

const PasswordStatusIcon = () => (
  <svg viewBox="0 0 18 18" aria-hidden="true">
    <path d="M9 2.2 14 4v3.6c0 3.1-1.9 5.9-5 7.2-3.1-1.3-5-4.1-5-7.2V4l5-1.8Z" />
    <path d="M9 6v3.2" />
    <path d="M9 11.5h.1" />
  </svg>
);

const validateImageFile = (file: File): string | null => {
  if (!VALID_IMAGE_TYPES.includes(file.type)) {
    return 'Only JPG, PNG, or GIF images are allowed.';
  }
  if (file.size > 5 * 1024 * 1024) {
    return 'Image size must be less than 5MB.';
  }
  return null;
};

export const Settings: React.FC = () => {
  const setUser = useAuthStore(state => state.setUser);
  const user = useAuthStore(state => state.user);

  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState>(null);

  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    branchName: '',
  });
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [passwordChangedAt, setPasswordChangedAt] = useState<string | null>(null);

  const [passwords, setPasswords] = useState({
    current: '',
    next: '',
    confirm: '',
  });
  const [passwordFieldErrors, setPasswordFieldErrors] = useState<{
    current?: string;
    next?: string;
    confirm?: string;
  }>({});
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [notificationPrefs, setNotificationPrefs] = useState<AdminNotificationSettings>({
    emailAlerts: true,
    taskReminders: true,
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);

  const photoInputRef = useRef<HTMLInputElement>(null);

  const applyProfileToState = useCallback(
    (data: Awaited<ReturnType<typeof profileService.getProfile>>) => {
      setProfile({
        fullName: data.fullName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        branchName: data.branch?.name ?? '—',
      });
      setProfilePictureUrl(data.profilePictureUrl);
      setPasswordChangedAt(data.passwordChangedAt);
      if (data.notificationSettings) {
        setNotificationPrefs(data.notificationSettings);
      }

      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        setUser({
          ...currentUser,
          name: data.fullName,
          avatar: data.profilePictureUrl ?? undefined,
        });
      }
    },
    [setUser]
  );

  const loadProfile = useCallback(async () => {
    setIsLoadingProfile(true);
    setLoadError(null);
    try {
      const data = await profileService.getProfile();
      applyProfileToState(data);
    } catch (error) {
      setLoadError(getProfileErrorMessage(error, 'Failed to load profile.'));
    } finally {
      setIsLoadingProfile(false);
    }
  }, [applyProfileToState]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (activeTab !== 'notifications') return;

    void profileService.getNotificationPreferences().then(setNotificationPrefs).catch(() => undefined);
  }, [activeTab]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const handleProfileChange =
    (field: 'fullName' | 'phoneNumber') => (event: React.ChangeEvent<HTMLInputElement>) => {
      setProfile(current => ({ ...current, [field]: event.target.value }));
    };

  const handlePasswordChange =
    (field: keyof typeof passwords) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setPasswordError(null);
      setPasswordFieldErrors(prev => ({ ...prev, [field]: undefined }));
      const value = event.target.value;
      setPasswords(current => ({ ...current, [field]: value }));

      if (field === 'confirm' && value) {
        setPasswordFieldErrors(prev => ({
          ...prev,
          confirm:
            passwords.next && value !== passwords.next ? 'Passwords do not match' : undefined,
        }));
      }

      if (field === 'next' && passwords.confirm) {
        setPasswordFieldErrors(prev => ({
          ...prev,
          confirm:
            value !== passwords.confirm ? 'Passwords do not match' : undefined,
        }));
      }
    };

  const handleSaveProfile = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const fullName = profile.fullName.trim();
      const phoneNumber = profile.phoneNumber.trim();

      if (!fullName) {
        setFeedback({ type: 'error', message: 'Full name is required.' });
        return;
      }
      if (!phoneNumber) {
        setFeedback({ type: 'error', message: 'Phone number is required.' });
        return;
      }

      setIsSavingProfile(true);
      try {
        const updated = await profileService.updateProfile({ fullName, phoneNumber });
        applyProfileToState(updated);
        setFeedback({ type: 'success', message: 'Profile updated successfully.' });
      } catch (error) {
        setFeedback({
          type: 'error',
          message: getProfileErrorMessage(error, 'Failed to save profile changes.'),
        });
      } finally {
        setIsSavingProfile(false);
      }
    },
    [applyProfileToState, profile.fullName, profile.phoneNumber]
  );

  const handlePhotoSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validationError = validateImageFile(file);
      if (validationError) {
        setFeedback({ type: 'error', message: validationError });
        return;
      }

      setIsUploadingPhoto(true);
      try {
        const uploadedFiles = await sharedService.uploadFiles([file]);
        const uploadedFile = uploadedFiles[0];
        if (!uploadedFile?.id) {
          throw new Error('Failed to upload photo.');
        }

        await profileService.updateProfilePicture({ profilePictureId: uploadedFile.id });
        setProfilePictureUrl(uploadedFile.url);
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          setUser({ ...currentUser, avatar: uploadedFile.url });
        }
        setFeedback({ type: 'success', message: 'Profile picture updated successfully.' });
      } catch (error) {
        setFeedback({
          type: 'error',
          message: getProfileErrorMessage(error, 'Failed to upload profile picture.'),
        });
      } finally {
        setIsUploadingPhoto(false);
        if (photoInputRef.current) {
          photoInputRef.current.value = '';
        }
      }
    },
    [setUser]
  );

  const handleRemovePhoto = useCallback(async () => {
    setIsRemovingPhoto(true);
    try {
      await profileService.removeProfilePicture();
      setProfilePictureUrl(null);
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        setUser({ ...currentUser, avatar: undefined });
      }
      setFeedback({ type: 'success', message: 'Profile picture removed successfully.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getProfileErrorMessage(error, 'Failed to remove profile picture.'),
      });
    } finally {
      setIsRemovingPhoto(false);
    }
  }, [setUser]);

  const handleUpdatePassword = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setPasswordError(null);
      setPasswordFieldErrors({});

      const fieldErrors: { current?: string; next?: string; confirm?: string } = {};

      if (!passwords.current.trim()) {
        fieldErrors.current = 'Current password is required.';
      }
      if (!isPasswordValid(passwords.next)) {
        fieldErrors.next = 'Password does not meet all requirements.';
      }
      if (!passwords.confirm) {
        fieldErrors.confirm = 'Please confirm your new password.';
      } else if (passwords.next !== passwords.confirm) {
        fieldErrors.confirm = 'New password and confirmation do not match.';
      }

      if (Object.keys(fieldErrors).length > 0) {
        setPasswordFieldErrors(fieldErrors);
        return;
      }

      setIsUpdatingPassword(true);
      try {
        await profileService.changePassword({
          currentPassword: passwords.current,
          newPassword: passwords.next,
        });
        setPasswords({ current: '', next: '', confirm: '' });
        setPasswordFieldErrors({});
        setShowPasswordRequirements(false);
        setPasswordChangedAt(new Date().toISOString());
        setFeedback({ type: 'success', message: 'Password changed successfully.' });
      } catch (error) {
        const apiFieldErrors = getFieldErrorsFromApi(error, {
          currentPassword: 'current',
          current_password: 'current',
          newPassword: 'next',
          new_password: 'next',
          confirmPassword: 'confirm',
          confirm_password: 'confirm',
        });

        if (Object.keys(apiFieldErrors).length > 0) {
          setPasswordFieldErrors({
            ...(apiFieldErrors.current ? { current: apiFieldErrors.current } : {}),
            ...(apiFieldErrors.next ? { next: apiFieldErrors.next } : {}),
            ...(apiFieldErrors.confirm ? { confirm: apiFieldErrors.confirm } : {}),
          });
        } else {
          setPasswordError(getProfileErrorMessage(error, 'Failed to update password.'));
        }
      } finally {
        setIsUpdatingPassword(false);
      }
    },
    [passwords]
  );

  const handleSaveNotifications = useCallback(async () => {
    setIsSavingNotifications(true);
    try {
      const updated = await profileService.updateNotificationPreferences(notificationPrefs);
      setNotificationPrefs(updated);
      setFeedback({ type: 'success', message: 'Notification preferences saved successfully.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getProfileErrorMessage(error, 'Failed to save notification preferences.'),
      });
    } finally {
      setIsSavingNotifications(false);
    }
  }, [notificationPrefs]);

  const profilePhoto = profilePictureUrl || user?.avatar || ASSETS.icons.createUser.person;
  const passwordStatusText = formatPasswordStatusText(passwordChangedAt);

  const canUpdatePassword =
    passwords.current.trim().length > 0 &&
    isPasswordValid(passwords.next) &&
    passwords.confirm.length > 0 &&
    passwords.next === passwords.confirm &&
    !passwordFieldErrors.current &&
    !passwordFieldErrors.next &&
    !passwordFieldErrors.confirm;

  const getPasswordSubmitDisabledReason = (): string | undefined => {
    if (isUpdatingPassword) return 'Updating password...';
    if (passwordFieldErrors.current) return passwordFieldErrors.current;
    if (passwordFieldErrors.next) return passwordFieldErrors.next;
    if (passwordFieldErrors.confirm) return passwordFieldErrors.confirm;
    if (!passwords.current.trim()) return 'Current password is required.';
    if (!isPasswordValid(passwords.next)) return 'Password does not meet all requirements.';
    if (!passwords.confirm) return 'Please confirm your new password.';
    if (passwords.next !== passwords.confirm) return 'New password and confirmation do not match.';
    return undefined;
  };

  const renderPasswordForm = () => (
    <form
      className="settings-card settings-card--security"
      onSubmit={handleUpdatePassword}
      noValidate
    >
      {passwordError && <p className="settings-inline-error">{passwordError}</p>}
      <div className="settings-form-grid settings-form-grid--security">
        <PasswordInput
          label="Current Password"
          value={passwords.current}
          onChange={handlePasswordChange('current')}
          placeholder="••••••••"
          autoComplete="current-password"
          error={passwordFieldErrors.current}
        />
        <div className="settings-password-field">
          <PasswordInput
            label="New Password"
            value={passwords.next}
            onChange={handlePasswordChange('next')}
            onFocus={() => setShowPasswordRequirements(true)}
            onBlur={() => setShowPasswordRequirements(false)}
            placeholder="Min. 15 characters"
            autoComplete="new-password"
            error={passwordFieldErrors.next}
          />
          {showPasswordRequirements && passwords.next && (
            <PasswordRequirements password={passwords.next} />
          )}
          {passwords.next && <PasswordStrength password={passwords.next} />}
        </div>
        <PasswordInput
          label="Confirm New Password"
          value={passwords.confirm}
          onChange={handlePasswordChange('confirm')}
          placeholder="Repeat password"
          autoComplete="new-password"
          error={passwordFieldErrors.confirm}
        />
      </div>

      <div className="settings-card__actions">
        <Button
          type="submit"
          className="settings-button settings-button--primary"
          disabled={isUpdatingPassword || !canUpdatePassword}
          disabledReason={getPasswordSubmitDisabledReason()}
        >
          {isUpdatingPassword ? <Spinner size="small" /> : null}
          {isUpdatingPassword ? 'Updating...' : 'Update Password'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="settings-page">
      <section className="settings-page__panel">
        <header className="settings-page__header">
          <h1>Account Settings</h1>
          <p>
            Manage your personal information, security preferences, and notification triggers.
          </p>
        </header>

        <div className="settings-page__content">
          <nav className="settings-page__tabs" aria-label="Settings sections">
            {SETTINGS_TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                className={`settings-page__tab${
                  activeTab === tab.key ? ' settings-page__tab--active' : ''
                }`}
                onClick={() => setActiveTab(tab.key)}
                aria-current={activeTab === tab.key ? 'page' : undefined}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {feedback && (
            <div
              className={`settings-page__feedback settings-page__feedback--${feedback.type}`}
              role="status"
              aria-live="polite"
            >
              {feedback.message}
            </div>
          )}

          {isLoadingProfile ? (
            <div className="settings-page__loading">
              <Spinner size="medium" />
              <p>Loading account settings...</p>
            </div>
          ) : loadError ? (
            <div className="settings-page__error">
              <p>{loadError}</p>
              <Button type="button" onClick={() => void loadProfile()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              {activeTab === 'profile' && (
                <>
                  <section className="settings-photo-card" aria-label="Profile photo">
                    <div className="settings-photo-avatar">
                      <img src={profilePhoto} alt={profile.fullName || 'Profile photo'} />
                    </div>
                    <div className="settings-photo-card__copy">
                      <strong>Profile Photo</strong>
                      <span>Recommended size: 400x400px. JPG, PNG or GIF.</span>
                      <div className="settings-photo-card__actions">
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          className="settings-photo-card__input"
                          onChange={handlePhotoSelect}
                        />
                        <Button
                          type="button"
                          size="small"
                          className="settings-button settings-button--primary"
                          disabled={isUploadingPhoto || isRemovingPhoto}
                          onClick={() => photoInputRef.current?.click()}
                        >
                          {isUploadingPhoto ? <Spinner size="small" /> : null}
                          {isUploadingPhoto ? 'Uploading...' : 'Upload New'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="small"
                          className="settings-button settings-button--secondary"
                          disabled={
                            isUploadingPhoto || isRemovingPhoto || !profilePictureUrl
                          }
                          onClick={() => void handleRemovePhoto()}
                        >
                          {isRemovingPhoto ? 'Removing...' : 'Remove'}
                        </Button>
                      </div>
                    </div>
                  </section>

                  <form className="settings-card" onSubmit={handleSaveProfile}>
                    <div className="settings-card__header">
                      <h2>Personal Details</h2>
                    </div>

                    <div className="settings-form-grid">
                      <Input
                        label="Full Name"
                        value={profile.fullName}
                        onChange={handleProfileChange('fullName')}
                        required
                      />
                      <Input
                        label="Email Address"
                        type="email"
                        value={profile.email}
                        disabled
                        readOnly
                      />
                      <Input
                        label="Phone Number"
                        value={profile.phoneNumber}
                        onChange={handleProfileChange('phoneNumber')}
                        required
                      />
                      <Input
                        label="Branch / Office Location"
                        value={profile.branchName}
                        disabled
                        readOnly
                      />
                    </div>

                    <div className="settings-card__actions">
                      <Button
                        type="submit"
                        className="settings-button settings-button--primary"
                        disabled={isSavingProfile}
                      >
                        {isSavingProfile ? <Spinner size="small" /> : null}
                        {isSavingProfile ? 'Saving...' : 'Save Profile Changes'}
                      </Button>
                    </div>
                  </form>
                </>
              )}

              {activeTab === 'security' && (
                <>
                  <section className="settings-card settings-card--status">
                    <div className="settings-status-heading">
                      <PasswordStatusIcon />
                      <h2>Password Status</h2>
                    </div>
                    <p className="settings-status-text">{passwordStatusText}</p>
                  </section>

                  <section className="settings-security-section">
                    <h2 className="settings-security-title">Security Settings</h2>
                    {renderPasswordForm()}
                  </section>
                </>
              )}

              {activeTab === 'notifications' && (
                <section className="settings-card">
                  <div className="settings-card__header">
                    <h2>Notification Triggers</h2>
                    <p className="settings-card__subtitle">
                      Choose which alerts you want to receive across the CRM.
                    </p>
                  </div>

                  <div className="settings-notification-list">
                    <Toggle
                      label="Email Alerts"
                      checked={notificationPrefs.emailAlerts}
                      onChange={checked =>
                        setNotificationPrefs(current => ({ ...current, emailAlerts: checked }))
                      }
                      className="settings-notification-toggle"
                    />
                    <Toggle
                      label="Task Reminders"
                      checked={notificationPrefs.taskReminders}
                      onChange={checked =>
                        setNotificationPrefs(current => ({ ...current, taskReminders: checked }))
                      }
                      className="settings-notification-toggle"
                    />
                  </div>

                  <div className="settings-card__actions">
                    <Button
                      type="button"
                      className="settings-button settings-button--primary"
                      disabled={isSavingNotifications}
                      onClick={() => void handleSaveNotifications()}
                    >
                      {isSavingNotifications ? <Spinner size="small" /> : null}
                      {isSavingNotifications ? 'Saving...' : 'Save Notification Preferences'}
                    </Button>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};
